import { AsyncPipe, NgForOf, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslocoModule } from '@ngneat/transloco';
import { Store } from '@ngrx/store';
import { TuiDestroyService, tuiIsPresent, TuiLetModule } from '@taiga-ui/cdk';
import {
    TuiButtonModule,
    TuiDataListModule,
    TuiGroupModule,
    TuiHostedDropdownModule,
    TuiTextfieldControllerModule,
    TuiTooltipModule
} from '@taiga-ui/core';
import { TuiInputModule } from '@taiga-ui/kit';
import {
    PositionActions,
    PositionStore,
    selectCurrentAddress,
    selectGeocoderResult,
    selectIsLoadingGeocode
} from '@us/web/position/data-access';
import {
    BehaviorSubject,
    debounceTime,
    distinctUntilChanged,
    filter,
    Observable,
    switchMap,
    take,
    takeUntil,
    tap,
    timer
} from 'rxjs';
import { provideComponentStore } from '@ngrx/component-store';
import { LocationRequestComponent } from '@us/web/position/feature';
import { provideCountriesMasks } from '@us/web/shared/feature';

@Component({
    selector: 'us-address-input-autocomplete',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        TranslocoModule,
        TuiInputModule,
        TuiTextfieldControllerModule,
        TuiHostedDropdownModule,
        LocationRequestComponent,
        AsyncPipe,
        TuiTooltipModule,
        TuiLetModule,
        TuiDataListModule,
        NgIf,
        NgForOf,
        TuiGroupModule,
        TuiButtonModule
    ],
    providers: [provideCountriesMasks, TuiDestroyService, provideComponentStore(PositionStore)],
    templateUrl: './address-input-autocomplete.component.html',
    styleUrls: ['./address-input-autocomplete.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddressInputAutocompleteComponent implements OnInit {
    private readonly store = inject(Store);
    private readonly positionStore = inject(PositionStore);
    private readonly destroy$ = inject(TuiDestroyService, { self: true });

    readonly MIN_LENGTH_TERM = 3;

    readonly searchCtrl = new FormControl('');
    readonly loading$ = new BehaviorSubject(false);
    readonly geocoderResults$: Observable<google.maps.GeocoderResult[]> = this.positionStore.geocoderResults$;

    readonly isLoadingGeocode$: Observable<boolean> = this.store.select(selectIsLoadingGeocode);

    private preventSearch = false;

    @Input() label?: string;

    ngOnInit() {
        this.setSearchValue();

        this.searchCtrl.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                filter(tuiIsPresent),
                debounceTime(300),
                filter(input => !this.preventSearch && input.length >= this.MIN_LENGTH_TERM),
                distinctUntilChanged(),
                tap(() => this.loading$.next(true)),
                tap(input => {
                    this.positionStore.autocomplete(input);
                })
            )
            .subscribe();

        // ? TMP fix multiple components on the same page
        this.store
            .select(selectGeocoderResult)
            .pipe(
                filter(tuiIsPresent),
                distinctUntilChanged(),
                takeUntil(this.destroy$),
                tap(gecode => {
                    this.searchCtrl.setValue(gecode.formatted_address);
                })
            )
            .subscribe();
    }

    onSelected(geocoderResult: google.maps.GeocoderResult) {
        this.store.dispatch(PositionActions.selectGeocode({ geocoderResult }));
    }

    getCurrentLocation() {
        this.store.dispatch(PositionActions.requestPosition({ force: true }));

        setTimeout(() => {
            this.setSearchValue();
        }, 1000);
    }

    private setSearchValue() {
        this.isLoadingGeocode$
            .pipe(
                filter(isLoading => !isLoading),
                switchMap(() => this.store.select(selectCurrentAddress)),
                takeUntil(this.destroy$),
                filter(tuiIsPresent),
                take(1),
                tap(address => {
                    this.searchCtrl.setValue(address);
                    this.preventSearch = true;
                }),
                switchMap(() => timer(1000)),
                tap(() => {
                    this.preventSearch = false;
                })
            )
            .subscribe();
    }
}
